import { Struct, Value } from "@bufbuild/protobuf";
import { atom } from "jotai";
import { Node } from "prosemirror-model";
import YAML from "yaml";

import {
	type HttpRequestPage,
	HttpRequestPageSchema,
	getDefaultHttpRequestPage,
	parsePage,
} from "@moment/api-collab";
import type { DocumentMetadata } from "@moment/api-collab/api-types";
import { Logger } from "@moment/logging";
import { schema } from "@moment/prosemirror-schema";

import {
	$document,
	type DocsListRemoteCollabEntry,
	docsListCollabEntry,
	docsListItemLocalEntry,
	docsListItemRemoteEntry,
} from "../document-list";
import { $applyExternalDocumentMetadata } from "../document-metadata/metadata-state.atom";
import { readTextFile } from "../filesystem";
import { EMPTY_DOC_JSON, type PageMetadataWithIndex } from "../page/page.types";
import { getPageDirPath, getPageFilePath } from "../paths";
import {
	$collabRegistryConnection,
	type CollabRegistryConnection,
} from "../reboot/collab-registry-connection.atom";
import { serializeForStruct } from "../reboot/utils/editor-state-utils";

const logger = new Logger("sync-new-pages-to-collab");

const COLLAB_SYNC_TIMEOUT_MS = 15_000;

export interface SyncNewPagesToCollabInput {
	documentId: string;
	newPages: PageMetadataWithIndex[];
	/**
	 * The metadata key used by $applyExternalDocumentMetadata.
	 * Required so we can apply the full metadata as a fallback on failure.
	 */
	metadataKey: string;
	/**
	 * The full, unmodified metadata from moment.yml (including the new pages).
	 * Applied locally as a fallback when collab sync fails, so new pages are
	 * never permanently dropped.
	 */
	fullMetadata: DocumentMetadata;
}

/**
 * Registers new pages with the Reboot collab server when they are created externally
 * (e.g., by an AI agent in the embedded terminal).
 *
 * The file watcher calls this via `set()` after detecting new pages in moment.yml. It:
 *   1. Checks if the document has an active collab session
 *   2. If so, calls `registry.createNewPage()` for each new page
 *   3. Reads page content from disk to include in the collab registration
 *
 * **Failure recovery:** If the collab sync cannot proceed (no registry connection,
 * missing document path) or if any page fails to create, the full metadata
 * (including the new pages) is applied locally as a fallback. This may cause
 * transient `StateNotConstructed` errors in `$documentLoaderEffect`, but those
 * are recoverable — permanently dropping pages from local state is not.
 *
 * For local-only documents (no collab), this is a no-op — the existing
 * $documentLoaderEffect handles loading from disk.
 */
export const $syncNewPagesToCollab = atom(
	null,
	async (get, set, input: SyncNewPagesToCollabInput) => {
		const { documentId, newPages, metadataKey, fullMetadata } = input;

		if (newPages.length === 0) {
			return;
		}

		const applyFullMetadataFallback = () => {
			logger.warn("Falling back to applying full metadata locally", {
				documentId,
				newPageIds: newPages.map((p) => p.id),
			});
			set($applyExternalDocumentMetadata, {
				key: metadataKey,
				metadata: fullMetadata,
			});
		};

		// Look up the document to check if collab is active
		const document = get($document(documentId));
		if (!document) {
			logger.debug("Document not found, skipping collab sync", { documentId });
			applyFullMetadataFallback();
			return;
		}

		const remoteEntry = docsListItemRemoteEntry(document);
		const collab = docsListCollabEntry(remoteEntry);
		if (!collab || !remoteEntry) {
			// No collab session — the caller already checked hasCollab before dispatching
			// this atom, so this indicates a race (document state changed between the
			// caller's check and this async execution). Apply fallback so pages aren't lost.
			logger.debug("No collab session found (possible race), applying fallback", {
				documentId,
			});
			applyFullMetadataFallback();
			return;
		}

		const workspaceSlug = remoteEntry.workspace.slug;
		const reg = get($collabRegistryConnection(workspaceSlug));
		if (!reg) {
			logger.warn("No collab registry connection, cannot sync to collab", {
				documentId,
				workspaceSlug,
			});
			applyFullMetadataFallback();
			return;
		}

		const localEntry = docsListItemLocalEntry(document);
		const documentPath = localEntry?.path;
		if (!documentPath) {
			logger.warn("No local document path, cannot sync to collab", { documentId });
			applyFullMetadataFallback();
			return;
		}

		logger.info("Syncing new pages to collab", {
			documentId,
			pageCount: newPages.length,
			pageIds: newPages.map((p) => p.id),
		});

		let hasFailures = false;
		for (const page of newPages) {
			try {
				await syncPageToCollab({
					page,
					documentId,
					documentPath,
					collab,
					reg,
				});
			} catch (err) {
				hasFailures = true;
				logger.error("Failed to sync page to collab", {
					documentId,
					pageId: page.id,
					pageType: page.type,
					err,
				});
			}
		}

		if (hasFailures) {
			applyFullMetadataFallback();
		}
	}
);
$syncNewPagesToCollab.debugLabel = "syncNewPagesToCollab";

async function syncPageToCollab({
	page,
	documentId,
	documentPath,
	collab,
	reg,
}: {
	page: PageMetadataWithIndex;
	documentId: string;
	documentPath: string;
	collab: DocsListRemoteCollabEntry;
	reg: CollabRegistryConnection;
}) {
	const pageType = page.type ?? "markdown";

	// Strip pageIndex from the metadata sent inside pageMetadataJson — it's
	// already passed as a separate top-level field in the createNewPage request
	// and shouldn't be persisted as part of the stored page metadata.
	const { pageIndex, ...pageMetadata } = page;

	const abort = new AbortController();
	const timeout = setTimeout(() => abort.abort(), COLLAB_SYNC_TIMEOUT_MS);
	try {
		if (pageType === "markdown") {
			// Read markdown content from disk
			let markdown = "";
			try {
				const pageFilePath = await getPageFilePath(documentPath, page.id, page.path);
				markdown = await readTextFile(pageFilePath);
			} catch (err) {
				// Page file may not exist yet if the agent writes moment.yml before the .md file.
				// Create with empty content — the file watcher will pick up the .md change later
				// and $applyExternalChange will sync the content.
				logger.debug("Could not read page file, using empty content", {
					documentId,
					pageId: page.id,
					err,
				});
			}

			let docNode: Node;
			if (markdown) {
				const { doc: parsedDoc } = parsePage(markdown);
				docNode = Node.fromJSON(schema, parsedDoc);
			} else {
				docNode = Node.fromJSON(schema, EMPTY_DOC_JSON);
			}

			logger.debug("Creating markdown page in collab registry", {
				documentId,
				pageId: page.id,
			});

			await reg.registry.createNewPage(
				reg.context,
				{
					documentId,
					branch: collab.branch,
					sha: collab.sha,
					pageIndex,
					pageMetadataJson: Value.fromJson(JSON.stringify(pageMetadata)),
					pageProsemirrorJson: Struct.fromJson(serializeForStruct(docNode.toJSON())),
				},
				{ signal: abort.signal }
			);

			logger.info("Markdown page synced to collab", { documentId, pageId: page.id });
		} else if (pageType === "request") {
			// Read request page content from disk
			let requestContent: HttpRequestPage = getDefaultHttpRequestPage();
			try {
				// Use explicit page.path if provided, otherwise fall back to default
				// request page location: pages/{pageId}/index.yml
				let pageFilePath: string;
				if (page.path) {
					pageFilePath = await getPageFilePath(documentPath, page.id, page.path);
				} else {
					const pageDirPath = await getPageDirPath(documentPath, page.id);
					pageFilePath = `${pageDirPath}/index.yml`;
				}
				const raw = await readTextFile(pageFilePath);
				if (raw) {
					const parsed = HttpRequestPageSchema.safeParse(YAML.parse(raw));
					if (parsed.success) {
						requestContent = parsed.data;
					}
				}
			} catch (err) {
				logger.debug("Could not read request page file, using defaults", {
					documentId,
					pageId: page.id,
					err,
				});
			}

			logger.debug("Creating request page in collab registry", {
				documentId,
				pageId: page.id,
			});

			await reg.registry.createNewPage(
				reg.context,
				{
					documentId,
					branch: collab.branch,
					sha: collab.sha,
					pageIndex,
					pageMetadataJson: Value.fromJson(JSON.stringify(pageMetadata)),
					pageProsemirrorJson: Struct.fromJson(serializeForStruct(EMPTY_DOC_JSON)),
					requestPageJson: Value.fromJson(requestContent),
				},
				{ signal: abort.signal }
			);

			logger.info("Request page synced to collab", { documentId, pageId: page.id });
		} else if (pageType === "canvas") {
			// Canvas pages use tldraw's own sync mechanism. We still need to create
			// the server-side entry so the metadata update is consistent.
			// For canvas pages, createNewPage with an empty doc is sufficient —
			// tldraw handles its own sync via $initializeCanvasSnapshotForCollab.
			logger.debug("Creating canvas page in collab registry", {
				documentId,
				pageId: page.id,
			});

			await reg.registry.createNewPage(
				reg.context,
				{
					documentId,
					branch: collab.branch,
					sha: collab.sha,
					pageIndex,
					pageMetadataJson: Value.fromJson(JSON.stringify(pageMetadata)),
					pageProsemirrorJson: Struct.fromJson(serializeForStruct(EMPTY_DOC_JSON)),
				},
				{ signal: abort.signal }
			);

			logger.info("Canvas page synced to collab", { documentId, pageId: page.id });
		} else {
			logger.warn("Unknown page type, skipping collab sync", {
				documentId,
				pageId: page.id,
				pageType,
			});
		}
	} finally {
		clearTimeout(timeout);
	}
}
